import React, { useState } from "react";
import { useNavigate, Routes, Route, useLocation } from "react-router-dom";
import styles from "./Home.module.css";
import Header from "../../components/header/Header";
import { ROUTES } from "../../constants/constants";
import Sidebar, { SidebarTab } from "./Sidebar";
import { IOrder, ICustomer, IInventory, IInquiry } from "../../types";
import TableInquiry from "./TableInquiry";
import TableOrder from "./TableOrder";
import TableCustomer from "./TableCustomer";
import TableInventory from "./TableInventory";
import TableAppointment from "./TableAppointment";
import Pagination from "../../components/Pagination/Pagination";
import { FiSearch } from "react-icons/fi";
import Button from "../../components/Button/Button";

function Home() {
  const location = useLocation();
  // Get tab from query param
  const params = new URLSearchParams(location.search);
  const tabParam = params.get("tab");
  const initialTab = tabParam
    ? ((tabParam.charAt(0).toUpperCase() + tabParam.slice(1)) as SidebarTab)
    : undefined;
  const [selectedTab, setSelectedTab] = useState<SidebarTab>(
    initialTab || "Inquiry"
  );
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  // Per-tab search state
  const [searchValue, setSearchValue] = useState<Record<SidebarTab, string>>({
    Inquiry: "",
    Appointment: "",
    Orders: "",
    Customers: "",
    Inventory: "",
  });
  const [searchInput, setSearchInput] = useState<Record<SidebarTab, string>>({
    Inquiry: "",
    Appointment: "",
    Orders: "",
    Customers: "",
    Inventory: "",
  });
  const navigate = useNavigate();
  let tableProps: any = {
    pageNumber,
    setTotalCount,
    searchValue: searchValue[selectedTab] || "",
  };
  let TableComponent: React.ReactNode = null;
  if (selectedTab === "Inquiry") {
    tableProps.onView = (row: IInquiry) => {
      navigate(ROUTES.INQUIRY_DETAIL.replace(":id", String(row.id)));
    };
    TableComponent = <TableInquiry {...tableProps} />;
  } else if (selectedTab === "Appointment") {
    TableComponent = <TableAppointment {...tableProps} />;
  } else if (selectedTab === "Orders") {
    tableProps.onView = (row: IOrder) => {
      navigate(ROUTES.ORDER_DETAIL.replace(":id", String(row.id)));
    };
    TableComponent = <TableOrder {...tableProps} />;
  } else if (selectedTab === "Customers") {
    tableProps.onView = (row: ICustomer) => {
      navigate(ROUTES.CUSTOMER_DETAIL.replace(":id", String(row.id)));
    };
    TableComponent = <TableCustomer {...tableProps} />;
  } else if (selectedTab === "Inventory") {
    tableProps.onView = (row: IInventory) => {
      navigate(ROUTES.INVENTORY_DETAIL.replace(":id", String(row.id)));
    };
    TableComponent = <TableInventory {...tableProps} />;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div className={styles.container}>
            <Header />
            <div className={styles.mainContent}>
              <div className={styles.leftPanel}>
                <Sidebar
                  selected={selectedTab}
                  onSelect={(tab) => {
                    setSelectedTab(tab);
                    setPageNumber(1);
                    // Clear search for the new tab
                    setSearchInput((prev) => ({ ...prev, [tab]: "" }));
                    setSearchValue((prev) => ({ ...prev, [tab]: "" }));
                    // Update the URL query parameter for tab
                    const params = new URLSearchParams(location.search);
                    params.set("tab", tab.toLowerCase());
                    navigate({ search: params.toString() }, { replace: true });
                  }}
                />
              </div>
              <div className={styles.rightPanel}>
                <div className={styles.topBarRow}>
                  <div className={styles.searchBarWrapper}>
                    <FiSearch className={styles.searchIconInField} />
                    <input
                      type="text"
                      className={styles.searchBar}
                      placeholder={`Search ${selectedTab}`}
                      value={searchInput[selectedTab] || ""}
                      onChange={(e) =>
                        setSearchInput((prev) => ({
                          ...prev,
                          [selectedTab]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setSearchValue((prev) => ({
                            ...prev,
                            [selectedTab]: searchInput[selectedTab] || "",
                          }));
                          setPageNumber(1);
                        }
                      }}
                    />
                    <Button
                      type="button"
                      className={styles.searchButton}
                      onClick={() => {
                        setSearchValue((prev) => ({
                          ...prev,
                          [selectedTab]: searchInput[selectedTab] || "",
                        }));
                        setPageNumber(1);
                      }}
                    >
                      Search
                    </Button>
                  </div>
                  <div className={styles.paginationBar}>
                    <Pagination
                      total={totalCount}
                      current={pageNumber}
                      onChange={setPageNumber}
                    />
                  </div>
                </div>
                <div className={styles.detailContent}>{TableComponent}</div>
              </div>
            </div>
          </div>
        }
      />
    </Routes>
  );
}

export default Home;
